/**
 * Event countdown helpers shared by the keyword show_event_time, the event list, the
 * header countdown and the routing triggers. Pure module: parseEventTime(naiveDateTime)
 * must return milliseconds since epoch, applying the event timezone.
 *
 * A countdown of countdown_time minutes starts at the checkout of the N-th played task
 * (N = countdown_task, at least 1), delivered by the server as first_task_checkout_time.
 * Before that checkout exists the countdown counts from now, like the native app.
 */

/** Date value from the server: not empty, not 0 and not the zero date. */
export function hasDateValue(value) {
    if (value === null || value === undefined) {
        return false;
    }
    const text = String(value).trim();
    return text !== '' && text !== '0' && !text.startsWith('0000-00-00');
}

export function hasCountdown(event) {
    return !!event && toNumber(event.countdown_time) > 0;
}

/** Start of the countdown: the N-th task checkout, or now while it does not exist yet. */
export function countdownStartMs(event, nowMs, parseEventTime) {
    if (event && hasDateValue(event.first_task_checkout_time)) {
        const start = parseEventTime(String(event.first_task_checkout_time));
        if (isFinite(start)) {
            return start;
        }
    }
    return nowMs;
}

/**
 * Remaining event time in milliseconds, null without a usable end. The end is the earlier
 * of the countdown end and the fixed event end (evenddatum + evende).
 */
export function remainingEventMillis(event, nowMs, parseEventTime) {
    if (!event) {
        return null;
    }
    const ends = [];
    if (hasCountdown(event)) {
        ends.push(countdownStartMs(event, nowMs, parseEventTime) + toNumber(event.countdown_time) * 60000);
    }
    const fixedEnd = fixedEndMs(event, parseEventTime);
    if (isFinite(fixedEnd)) {
        ends.push(fixedEnd);
    }
    if (ends.length === 0) {
        return null;
    }
    return Math.max(0, Math.min.apply(null, ends) - nowMs);
}

/** Remaining event time in whole minutes (rounded down), null without a usable end. */
export function remainingEventMinutes(event, nowMs, parseEventTime) {
    const ms = remainingEventMillis(event, nowMs, parseEventTime);
    return ms === null ? null : Math.floor(ms / 60000);
}

/**
 * checkout_time of the N-th earliest checked-out spot (N = countdownTask, at least 1).
 * Any status counts, like on the server; null while fewer spots are checked out.
 */
export function nthCheckoutTime(spots, countdownTask, parseTime) {
    const count = Math.max(1, toNumber(countdownTask));
    const checkouts = (Array.isArray(spots) ? spots : [])
        .filter((spot) => spot && hasDateValue(spot.checkout_time))
        .map((spot) => ({ value: String(spot.checkout_time), ms: parseTime(String(spot.checkout_time)) }))
        .filter((entry) => isFinite(entry.ms))
        .sort((a, b) => a.ms - b.ms);
    return checkouts.length >= count ? checkouts[count - 1].value : null;
}

/**
 * Window for the header countdown. Without a countdown the fixed event window; once the
 * N-th checkout exists the countdown window capped by the fixed end; before that the fixed
 * window plus the full countdown as frozenMs. Unparsable dates are null.
 */
export function countdownWindow(event, parseEventTime) {
    const fixedStart = finiteOrNull(fixedStartMs(event, parseEventTime));
    const fixedEnd = finiteOrNull(fixedEndMs(event, parseEventTime));
    if (!hasCountdown(event)) {
        return { startMs: fixedStart, endMs: fixedEnd, frozenMs: null };
    }
    const countdownMs = toNumber(event.countdown_time) * 60000;
    const start = hasDateValue(event.first_task_checkout_time)
        ? parseEventTime(String(event.first_task_checkout_time))
        : NaN;
    if (!isFinite(start)) {
        return { startMs: fixedStart, endMs: fixedEnd, frozenMs: countdownMs };
    }
    const countdownEnd = start + countdownMs;
    return {
        startMs: start,
        endMs: fixedEnd === null ? countdownEnd : Math.min(countdownEnd, fixedEnd),
        frozenMs: null,
    };
}

function fixedStartMs(event, parseEventTime) {
    return event && event.evdatum && event.evstart ? parseEventTime(event.evdatum + ' ' + event.evstart) : NaN;
}

function fixedEndMs(event, parseEventTime) {
    return event && event.evenddatum && event.evende ? parseEventTime(event.evenddatum + ' ' + event.evende) : NaN;
}

function finiteOrNull(value) {
    return isFinite(value) ? value : null;
}

function toNumber(value) {
    const number = typeof value === 'number' ? value : parseFloat(value);
    return isFinite(number) ? number : 0;
}
